import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationService } from 'services/notification-service';
import { ToolsService } from 'services/tools-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { ToolConfig } from 'shared/interfaces/tools/Tool';

interface UseManageToolConfigsProps {
  smartAssistantId: string;
  existingToolConfigs: ToolConfig[];
}

export function useManageToolConfigs({ smartAssistantId, existingToolConfigs }: UseManageToolConfigsProps) {
  const queryClient = useQueryClient();

  const updateToolConfigsMutation = useMutation({
    mutationFn: (toolConfigs: ToolConfig[]) => {
      return ToolsService.updateAllToolConfigs({
        smartAssistantId,
        toolConfigs
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SMART_ASSISTANT_QUERY_KEY, { smartAssistantId }]
      });
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_TOOLS');
    },
    onError: () => {
      NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_TOOLS');
    }
  });

  const updateToolConfigs = async (newToolConfigs: ToolConfig[]) => {
    const toolConfigsToUpdate = newToolConfigs.filter((toolConfig) => toolConfig._id);
    const toolConfigsToAdd = newToolConfigs.filter((toolConfig) => !toolConfig._id);

    const finalToolConfigs = [...existingToolConfigs, ...toolConfigsToAdd];

    for (const toolConfig of toolConfigsToUpdate) {
      const index = finalToolConfigs.findIndex((config) => config._id === toolConfig._id);
      if (index !== -1) {
        finalToolConfigs[index] = toolConfig;
      }
    }

    // Overwrite the existing tool configs with the new ones
    await updateToolConfigsMutation.mutateAsync(finalToolConfigs);
  };

  const deleteToolConfigs = async (toolConfigsToDelete: ToolConfig[]) => {
    const toolConfigIdsToRemove = toolConfigsToDelete.map((toolConfig) => toolConfig._id);
    const finalToolConfigs = existingToolConfigs.filter(
      (toolConfig) => !toolConfigIdsToRemove.includes(toolConfig._id)
    );
    await updateToolConfigsMutation.mutateAsync(finalToolConfigs);
  };

  return { updateToolConfigs, deleteToolConfigs };
}
